import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { t } from 'ttag';

import { Menu } from '@headlessui/react';
import {
  RiArrowDownSLine,
  RiArrowLeftSLine,
  RiArrowRightSLine
} from 'react-icons/ri';

export default function Pagination({ push, currentPage, totalPages }) {
  const buttonClassNames = useMemo(
    () =>
      clsx(
        'flex h-10 min-w-[40px] cursor-pointer appearance-none items-center justify-center space-x-2 border border-solid border-gray bg-white px-2 py-2 text-left text-sm text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-blue focus-visible:ring-offset-2 enabled:hover:bg-light-white-smoke enabled:active:bg-white-smoke disabled:cursor-not-allowed disabled:opacity-50'
      ),
    []
  );

  const getUrl = useCallback(
    page => {
      const url = new URL(window.location.href);
      url.searchParams.set('page', page);
      return url;
    },
    [push]
  );

  /**
   * @param {string} page - The page to navigate to.
   * @type {function(string): void}
   */
  const handlePageChange = useCallback(
    page => {
      const url = new URL(window.location.href);
      url.searchParams.set('page', page);
      push(url);

      window?.scrollTo({
        top: 0,
        left: 0
      });
    },
    [push]
  );

  return (
    <div className="flex max-w-screen-sm items-center space-x-4 p-4">
      <a
        href={getUrl((parseInt(currentPage, 10) - 1).toString())}
        aria-label={t`Previous Page`}
        onClick={event => {
          event.preventDefault();
          handlePageChange((parseInt(currentPage, 10) - 1).toString());
        }}
        className={clsx(
          'flex h-10 min-w-[40px] cursor-pointer appearance-none items-center justify-center space-x-2 border border-solid border-gray bg-white px-2 py-2 text-left text-sm text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-blue focus-visible:ring-offset-2',
          {
            'cursor-not-allowed opacity-50 pointer-events-none':
              currentPage === '1',
            'hover:bg-light-white-smoke active:bg-white-smoke':
              currentPage !== '1'
          }
        )}
      >
        <RiArrowLeftSLine className="h-4 w-4 shrink-0" />
      </a>
      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button className={buttonClassNames}>
          <span>{t`Page ${currentPage} of ${totalPages}`}</span>
          <RiArrowDownSLine className="h-4 w-4 shrink-0 text-dark-gray" />
        </Menu.Button>
        <Menu.Items className="absolute right-0 z-10 mt-2 max-h-[224px] w-full origin-top-right divide-y divide-whisper overflow-y-scroll bg-white shadow-xl ring-1 ring-gray focus:outline-none">
          {
            /** @type {number[]} */
            Array.from(Array(parseInt(totalPages, 10)).keys()).map(index => (
              <Menu.Item key={index}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => handlePageChange(`${index + 1}`)}
                    className={clsx(
                      'flex w-full items-center justify-start gap-1 border-none px-4 py-2 text-sm',
                      {
                        'bg-light-white-smoke': active,
                        'bg-transparent': !active
                      }
                    )}
                  >
                    <span>{t`Page`}</span>
                    <span>{index + 1}</span>
                  </button>
                )}
              </Menu.Item>
            ))
          }
        </Menu.Items>
      </Menu>
      <a
        href={getUrl((parseInt(currentPage, 10) + 1).toString())}
        aria-label={t`Next Page`}
        onClick={event => {
          event.preventDefault();
          handlePageChange((parseInt(currentPage, 10) + 1).toString());
        }}
        className={clsx(
          'flex h-10 min-w-[40px] cursor-pointer appearance-none items-center justify-center space-x-2 border border-solid border-gray bg-white px-2 py-2 text-left text-sm text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-blue focus-visible:ring-offset-2',
          {
            'cursor-not-allowed opacity-50 pointer-events-none':
              currentPage === totalPages,
            'hover:bg-light-white-smoke active:bg-white-smoke':
              currentPage !== totalPages
          }
        )}
      >
        <RiArrowRightSLine className="h-4 w-4 shrink-0" />
      </a>
    </div>
  );
}

Pagination.propTypes = {
  /**
   * The Push Function of the current Page.
   */
  push: PropTypes.func.isRequired,
  /**
   * The current Page Number.
   */
  currentPage: PropTypes.string.isRequired,
  /**
   * The Total Number of Pages.
   */
  totalPages: PropTypes.string.isRequired
};
